import { useEffect, useState } from "react";
import { Loader2, TrendingUp, Users } from "lucide-react";
import { api } from "@/lib/api";

const Figure = ({ label, value, note, testId }) => (
  <p className="text-sm" data-testid={testId}>
    <span className="block font-display text-3xl font-bold text-brand">{value}</span>
    <span className="text-ink-soft">{label}</span>
    {note ? <span className="block text-[11px] text-ink-soft">{note}</span> : null}
  </p>
);

// Did the people who watched the tour go on to pay — and did the ones who never watched?
export const WatchThenUpgrade = () => {
  const [stats, setStats] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    setStats(null);
    api.get("/tour/conversion", { params: { days } })
      .then(({ data }) => setStats(data))
      .catch((e) => console.error("could not load the tour conversion", e));
  }, [days]);

  const cut = stats?.pricing;
  const refs = stats?.referrals;

  return (
    <div className="rounded-3xl border border-white/10 bg-panel p-6" data-testid="watch-then-upgrade">
      <div className="flex flex-wrap items-center gap-3">
        <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-ink-soft">
          <TrendingUp className="h-4 w-4 text-brand" /> Watch, then upgrade
        </p>
        <div className="ms-auto flex gap-1">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              data-testid={`conversion-days-${d}`}
              onClick={() => setDays(d)}
              className={`min-h-[28px] rounded-full border px-3 text-[11px] transition-colors ${
                days === d ? "border-brand bg-brand/15 text-brand"
                  : "border-white/15 text-ink-soft hover:text-ink"}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {!stats ? (
        <Loader2 className="mt-4 h-4 w-4 animate-spin text-ink-soft" />
      ) : (
        <>
          <p className="mt-3 text-xs text-ink-soft">On the plans page, last {days} days</p>
          <div className="mt-3 flex flex-wrap gap-6">
            <Figure testId="conversion-watchers" value={cut.watchers} label="watched there"
                    note={`${cut.signed_in} of them signed in`} />
            <Figure testId="conversion-paid" value={cut.paid} label="then paid"
                    note={cut.revenue ? `$${cut.revenue} taken` : "no money yet"} />
            <Figure testId="conversion-rate" value={`${cut.through}%`} label="watch → pay"
                    note={cut.hours_to_pay != null ? `about ${cut.hours_to_pay}h to decide` : ""} />
          </div>

          <div className="mt-5 rounded-2xl border border-white/10 bg-page p-4">
            <p className="text-xs text-ink-soft">
              Everyone who paid in the last {days} days
            </p>
            <div className="mt-3 flex flex-wrap gap-6">
              <Figure testId="conversion-payers" value={stats.payers} label="paid for a plan" />
              <Figure testId="conversion-payers-watched" value={stats.payers_who_watched}
                      label="had watched the tour"
                      note={`${stats.share_of_payers_watching}% of everyone who paid`} />
              <Figure testId="conversion-payers-cold" value={stats.payers_who_never_watched}
                      label="never watched it" />
            </div>
          </div>

          <div className="mt-5" data-testid="conversion-chapters">
            <p className="text-xs text-ink-soft">
              {stats.decider
                ? `Last chapter before paying — most decide on “${stats.decider.title}”`
                : "Last chapter before paying — nobody has paid after watching yet"}
            </p>
            <ul className="mt-2 space-y-1.5">
              {stats.chapters.map((c, i) => (
                <li key={c.key} className="flex items-center gap-2 text-[11px]"
                    data-testid={`conversion-chapter-${c.key}`}>
                  <span className="w-6 shrink-0 tabular-nums text-ink-soft">{i + 1}</span>
                  <span className="w-52 shrink-0 truncate text-ink" title={c.title}>{c.title}</span>
                  <span className="h-1.5 min-w-[3px] rounded-full bg-brand/70"
                        style={{ width: `${Math.max(3, (c.paid_here / Math.max(1, stats.decider?.paid_here || 1)) * 100)}%` }} />
                  <span className="shrink-0 tabular-nums text-ink-soft">
                    {c.paid_here} paid here{c.revenue ? ` · $${c.revenue}` : ""}
                    <span className="text-white/25"> · {c.watchers} got this far</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {stats.languages.length > 0 && (
            <p className="mt-4 flex flex-wrap items-center gap-2 text-[11px] text-ink-soft"
               data-testid="conversion-languages">
              By language:
              {stats.languages.map((row) => (
                <span key={row.lang} className="rounded-full border border-white/10 px-2 py-0.5">
                  {row.lang} · {row.watchers} watched{row.paid ? ` · ${row.paid} paid` : ""}
                </span>
              ))}
            </p>
          )}

          <div className="mt-5 rounded-2xl border border-white/10 bg-page p-4" data-testid="referral-nudge-stats">
            <p className="flex items-center gap-2 text-xs text-ink-soft">
              <Users className="h-3.5 w-3.5 text-brand" /> Tours passed to a friend
            </p>
            <div className="mt-3 flex flex-wrap gap-6">
              <Figure testId="referral-shares" value={refs.shares} label="shared by members" />
              <Figure testId="referral-watched" value={refs.watched} label="friends watched" />
              <Figure testId="referral-joined" value={refs.joined} label="friends joined"
                      note={refs.upgraded ? `${refs.upgraded} of them are paying` : ""} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};
